import { Button, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@heroui/react'
import { Loading } from 'components/core/Loading'
import FileUploader from 'components/Dashboard/ConvoPathways/Components/uploadJson'
import { Upload } from 'lucide-react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

export default function ImportPathwayModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  jsonContent,
  setJsonContent,
}) {
  const importMethods = useForm({
    defaultValues: {
      pathwayName: '',
    },
  })

  const handleSubmit = async (data) => {
    if (!jsonContent) {
      toast.error('Please upload a JSON file first')
      return
    }

    onSubmit({ type: 'json', pathwayName: data.pathwayName })
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="4xl"
      classNames={{
        base: 'max-w-6xl',
        body: 'p-6',
        header: 'border-b border-gray-200',
      }}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <h2 className="text-2xl font-semibold">Import Pathway</h2>
          <p className="text-sm text-gray-600">
            Upload a file to bring in an existing pathway and start editing it on the canvas.
          </p>
        </ModalHeader>
        <ModalBody>
          <div className="flex gap-8">
            {/* Left side - Form */}
            <div className="flex-1">
              <FormProvider {...importMethods}>
                <form
                  onSubmit={importMethods.handleSubmit(handleSubmit)}
                  className="flex flex-col gap-y-6"
                >
                  <div>
                    <label className={`
                      mb-2 block text-sm font-medium text-gray-900
                    `}
                    >
                      Pathway Title
                    </label>
                    <Input
                      placeholder="Bland Bistro"
                      variant="bordered"
                      radius="sm"
                      {...importMethods.register('pathwayName', { required: 'Pathway name is required' })}
                    />
                  </div>

                  <div>
                    <label className={`
                      mb-2 block text-sm font-medium text-gray-900
                    `}
                    >
                      Upload JSON File
                    </label>
                    <p className="mb-3 text-sm text-gray-600">Upload your pathway file (JSON format).</p>
                    <div className="h-64 min-h-64">
                      <div className="h-full">
                        <FileUploader
                          onDataLoaded={() => toast.success('File uploaded successfully')}
                          jsonContent={jsonContent}
                          setJsonContent={setJsonContent}
                        />
                      </div>
                    </div>
                  </div>
                </form>
              </FormProvider>
            </div>

            {/* Right side - Guidelines */}
            <div className="w-80 rounded-lg bg-gray-50 p-6">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">Guidelines</h3>
              <div className="flex flex-col gap-y-3 text-sm text-gray-700">
                <div className="flex items-start gap-3">
                  <span className={`
                    flex h-6 w-6 shrink-0 items-center justify-center
                    rounded-full bg-blue-100 text-xs font-medium text-blue-600
                  `}
                  >
                    1
                  </span>
                  <span>Upload a valid pathway file in JSON format.</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className={`
                    flex h-6 w-6 shrink-0 items-center justify-center
                    rounded-full bg-blue-100 text-xs font-medium text-blue-600
                  `}
                  >
                    2
                  </span>
                  <span>Make sure the file includes all required steps and settings.</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className={`
                    flex h-6 w-6 shrink-0 items-center justify-center
                    rounded-full bg-blue-100 text-xs font-medium text-blue-600
                  `}
                  >
                    3
                  </span>
                  <span>Check that your file follows the current Bland format version.</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className={`
                    flex h-6 w-6 shrink-0 items-center justify-center
                    rounded-full bg-blue-100 text-xs font-medium text-blue-600
                  `}
                  >
                    4
                  </span>
                  <span>Each pathway should have a clear starting point.</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className={`
                    flex h-6 w-6 shrink-0 items-center justify-center
                    rounded-full bg-blue-100 text-xs font-medium text-blue-600
                  `}
                  >
                    5
                  </span>
                  <span>Avoid uploading incomplete or corrupted files.</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className={`
                    flex h-6 w-6 shrink-0 items-center justify-center
                    rounded-full bg-blue-100 text-xs font-medium text-blue-600
                  `}
                  >
                    6
                  </span>
                  <span>You can edit and test your imported pathway after uploading.</span>
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="bordered" onPress={onClose} radius="none">
            Cancel
          </Button>
          <Button
            radius="none"
            onPress={importMethods.handleSubmit(handleSubmit)}
            isLoading={isLoading}
            className={`
              bg-[#030712] text-white shadow-md transition-transform
              hover:scale-105
            `}
            startContent={!isLoading && <Upload size={16} />}
          >
            {isLoading ? <Loading /> : 'Import Pathway'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
